import { useEffect, useRef, useState } from 'react';
import { petError } from './model.js';
export function hasPetMissions(catalog) {
    return catalog?.capabilities.enabled === true && catalog.capabilities.missions?.available === true;
}
export function supportsPetExecutions(catalog) {
    return hasPetMissions(catalog) && catalog?.capabilities.missions?.executions?.supported === true;
}
export function hasPetExecutions(catalog) {
    return supportsPetExecutions(catalog) && catalog?.capabilities.missions?.executions?.available === true;
}
export function missionTime(value) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'Time unavailable' : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}
export function describeMissionChange(patch) {
    return patch.kind === 'remember-preference' ? `Keep this preference for ${patch.scope === 'pet' ? 'this companion' : 'this mission'}: ${patch.text}`
        : patch.kind === 'require-topic' ? `Also require this topic, in addition to every current filter: ${patch.topic}`
            : patch.kind === 'release-topic' ? `Remove the additional required topic “${patch.topic}”. Existing Setup sources, filters and permissions stay unchanged.`
                : patch.kind === 'temporary-focus' ? `Prioritize “${patch.topic}” from ${new Date(patch.startsAt).toLocaleString(undefined, { timeZone: patch.timeZone })} until ${new Date(patch.expiresAt).toLocaleString(undefined, { timeZone: patch.timeZone })} (${patch.timeZone}). Other authorized topics remain eligible; permissions and budgets stay unchanged.` : 'Pause this mission.';
}
/** View polling does not run in hidden/unmounted views or represent background execution. */
export function useMissionRefresh(enabled, refresh) {
    const callback = useRef(refresh);
    callback.current = refresh;
    useEffect(() => {
        if (!enabled)
            return;
        const visible = () => { if (document.visibilityState !== 'hidden')
            callback.current(); };
        const timer = setInterval(visible, 30000);
        document.addEventListener('visibilitychange', visible);
        return () => { clearInterval(timer); document.removeEventListener('visibilitychange', visible); };
    }, [enabled]);
}
// Bound to the authenticated API object, never local/session storage. Leaving a view cannot mint a duplicate request.
const pendingSessions = new WeakMap();
const refusalCodes = new Set(['mission_revision_conflict', 'memory_revision_conflict', 'decision_stale', 'decision_expired', 'mission_target_stale', 'mission_definition_stale', 'mission_authority_stale', 'mission_state_conflict', 'mission_closed', 'mission_exists', 'memory_capacity', 'memory_forgotten', 'steering_stale', 'mission_filter_capacity', 'execution_policy_stale', 'execution_revision_conflict', 'execution_target_stale', 'execution_state_conflict', 'execution_review_pending', 'execution_review_stale', 'execution_budget_invalid', 'action_policy_required', 'continuity_context_stale', 'schedule_revision_conflict', 'schedule_authority_stale', 'priority_revision_conflict', 'priority_set_conflict', 'finding_revision_conflict', 'continuity_unavailable', 'wakes_disabled', 'wake_policy_required', 'schedule_invalid', 'next_check_stale', 'schedule_capacity', 'priority_capacity', 'priority_invalid']);
function knownRefusal(error) {
    if (!error || typeof error !== 'object')
        return false;
    const value = error;
    return typeof value.code === 'string' && (value.status === 409 && refusalCodes.has(value.code) || value.status === 503 && ['continuity_unavailable', 'wakes_disabled'].includes(value.code));
}
/** A retry retains the original payload closure and request ID, even after a revision refresh. */
export function useMissionAction(api, scope, onRecovered = () => { }) {
    const identity = useRef({ api, scope });
    if (identity.current.api !== api || identity.current.scope !== scope)
        identity.current = { api, scope };
    const current = identity.current;
    const active = useRef(null);
    const recovered = useRef(onRecovered);
    recovered.current = onRecovered;
    let session = pendingSessions.get(api);
    if (!session) {
        session = new Map();
        pendingSessions.set(api, session);
    }
    const pending = session;
    const [state, setState] = useState({ owner: current, busy: false, error: '', unresolved: false });
    const visibleState = state.owner === current ? state : { owner: current, busy: false, error: '', unresolved: false };
    const visible = pending.has(scope) && !visibleState.busy && !visibleState.error ? { ...visibleState, unresolved: true, error: 'An earlier request from this session still needs its original receipt.' } : visibleState;
    useEffect(() => () => { active.current?.abort(); active.current = null; }, [api, scope]);
    async function execute(operation, lookup) {
        if (active.current)
            return;
        const controller = new AbortController();
        active.current = controller;
        setState(previous => ({ owner: current, busy: true, error: '', receipt: previous.owner === current ? previous.receipt : undefined, unresolved: true }));
        try {
            const result = lookup ? await api.receipt(operation.id, controller.signal) : await operation.call(operation.id, controller.signal);
            if (controller.signal.aborted || identity.current !== current)
                return;
            if (result.receipt.clientRequestId !== operation.id)
                throw new Error('The service returned a receipt for a different request. Check the original outcome.');
            if (['mission.chat', 'execution.create'].includes(result.receipt.kind) && result.receipt.state === 'recorded') {
                setState({ owner: current, busy: false, error: result.receipt.kind === 'execution.create' ? 'Your original execution request is recorded, but the plan is not confirmed. Check this request before starting another.' : 'Your original message is recorded, but its response is not confirmed. Keep checking this request; no new message has been submitted.', receipt: result.receipt, unresolved: true });
                return;
            }
            pending.delete(scope);
            setState({ owner: current, busy: false, error: '', receipt: result.receipt, unresolved: false });
            if (operation.owner === current)
                operation.complete(result.receipt);
            else
                recovered.current(result.receipt);
        }
        catch (error) {
            if (!controller.signal.aborted && identity.current === current) {
                const refused = !lookup && knownRefusal(error);
                if (refused)
                    pending.delete(scope);
                setState({ owner: current, busy: false, error: petError(error), unresolved: !refused });
            }
        }
        finally {
            if (active.current === controller)
                active.current = null;
        }
    }
    function run(call, complete = () => { }) {
        if (active.current || pending.has(scope))
            return;
        const operation = { id: crypto.randomUUID(), call, complete, owner: current };
        pending.set(scope, operation);
        void execute(operation, false);
    }
    return { ...visible, run,
        retry: () => { const operation = pending.get(scope); if (operation)
            void execute(operation, false); },
        lookup: () => { const operation = pending.get(scope); if (operation)
            void execute(operation, true); },
    };
}
//# sourceMappingURL=missionState.js.map